import React, { useState } from 'react';
import './StarRating.css';
import { submitAnalysisRating } from '../../services/analysis.service';

interface StarRatingProps {
  analysisId: string;
  existingRating?: number;
  existingComment?: string;
}

const LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

const StarRating: React.FC<StarRatingProps> = ({ analysisId, existingRating, existingComment }) => {
  const [hovered, setHovered] = useState(0);
  const [selected, setSelected] = useState(existingRating || 0);
  const [comment, setComment] = useState(existingComment || '');
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(!!existingRating);
  const [error, setError] = useState<string | null>(null);

  const handleStarClick = (star: number) => {
    if (submitted) return;
    setSelected(star);
    setShowForm(true);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!selected) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await submitAnalysisRating(analysisId, selected, comment || undefined);
      setSubmitted(true);
      setShowForm(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit rating');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setSelected(existingRating || 0);
    setComment(existingComment || '');
    setError(null);
  };

  const displayRating = submitted ? selected : (hovered || selected);

  return (
    <div className="star-rating-container">
      <h4 className="star-rating-title">
        {submitted ? 'Your Rating' : 'Rate this Analysis'}
      </h4>
      <div className="stars-row">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            className={`star-btn ${star <= displayRating ? 'filled' : ''} ${submitted ? 'readonly' : ''}`}
            onMouseEnter={() => !submitted && setHovered(star)}
            onMouseLeave={() => !submitted && setHovered(0)}
            onClick={() => handleStarClick(star)}
            disabled={submitted}
            aria-label={`${star} star${star > 1 ? 's' : ''}`}
          >
            ★
          </button>
        ))}
        {displayRating > 0 && (
          <span className="star-label">{LABELS[displayRating]}</span>
        )}
      </div>

      {submitted && (existingComment || comment) && (
        <p className="star-submitted-comment">"{existingComment || comment}"</p>
      )}

      {submitted && (
        <p className="star-submitted-note">Thanks for your feedback!</p>
      )}

      {showForm && !submitted && (
        <div className="star-form">
          <textarea
            className="star-comment-input"
            placeholder="Add a comment (optional)..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
          />
          {error && <p className="star-error">{error}</p>}
          <div className="star-form-actions">
            <button
              className="dark-button secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              className="dark-button primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !selected}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Rating'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StarRating;
